import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Lock, Eye, EyeOff, Check, AlertCircle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" }); // 'success' | 'error' | null

  // Password validation
  const [passwordChecks, setPasswordChecks] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });

  useEffect(() => {
    const password = formData.newPassword;
    setPasswordChecks({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [formData.newPassword]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (status.type === "error") {
      setStatus({ type: null, message: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!token) {
      setStatus({ type: "error", message: "Token mancante. Richiedi un nuovo link di reset." });
      return;
    }

    if (formData.newPassword.length < 8) {
      setStatus({ type: "error", message: "La password deve contenere almeno 8 caratteri." });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setStatus({ type: "error", message: "Le password non corrispondono." });
      return;
    }

    setIsLoading(true);
    setStatus({ type: null, message: "" });

    try {
      const response = await fetch("https://nerbys-app-production.up.railway.app/api/auth/reset-password-confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token: token,
          new_password: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: "success", message: data.message || "Password aggiornata con successo!" });
        setFormData({ newPassword: "", confirmPassword: "" });
      } else {
        setStatus({ type: "error", message: data.detail || "Errore durante il reset della password." });
      }
    } catch (error) {
      setStatus({ type: "error", message: "Errore di connessione. Riprova più tardi." });
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <main data-testid="reset-password-page" className="pt-24 md:pt-32 pb-20 min-h-screen">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Link non valido</h1>
            <p className="text-purple-200/60 mb-6">
              Il link di reset password non è valido o è incompleto. 
              Richiedi un nuovo link di reset.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna alla home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="reset-password-page" className="pt-24 md:pt-32 pb-20 min-h-screen">
      <div className="max-w-md mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-2">
            Reimposta Password
          </h1>
          <p className="text-purple-200/60">
            Inserisci la tua nuova password
          </p>
        </div>

        {/* Success State */}
        {status.type === "success" ? (
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Password Aggiornata!</h2>
            <p className="text-purple-200/60 mb-6">
              {status.message}
            </p>
            <p className="text-purple-200/50 text-sm">
              Ora puoi accedere all'app Nerbys con la tua nuova password.
            </p>
          </div>
        ) : (
          /* Form */
          <form 
            onSubmit={handleSubmit}
            data-testid="reset-password-form"
            className="bg-purple-950/30 border border-purple-500/10 rounded-2xl p-6 md:p-8 space-y-6"
          >
            {/* Error Message */}
            {status.type === "error" && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{status.message}</p>
              </div>
            )}

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-purple-200/70 mb-2">
                Nuova Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  required
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  data-testid="new-password-input"
                  className="w-full bg-purple-950/50 border border-purple-500/20 text-white rounded-xl px-4 py-3 pr-12 focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-purple-200/30 transition-all"
                  placeholder="Inserisci la nuova password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-purple-200/50 mb-2">Requisiti password:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.minLength ? 'text-green-400' : 'text-purple-200/40'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.minLength ? 'bg-green-500/20' : 'bg-purple-500/10'}`}>
                    {passwordChecks.minLength && <Check className="w-3 h-3" />}
                  </div>
                  Min. 8 caratteri
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.hasUppercase ? 'text-green-400' : 'text-purple-200/40'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.hasUppercase ? 'bg-green-500/20' : 'bg-purple-500/10'}`}>
                    {passwordChecks.hasUppercase && <Check className="w-3 h-3" />}
                  </div>
                  Maiuscola
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.hasLowercase ? 'text-green-400' : 'text-purple-200/40'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.hasLowercase ? 'bg-green-500/20' : 'bg-purple-500/10'}`}>
                    {passwordChecks.hasLowercase && <Check className="w-3 h-3" />}
                  </div>
                  Minuscola
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.hasNumber ? 'text-green-400' : 'text-purple-200/40'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.hasNumber ? 'bg-green-500/20' : 'bg-purple-500/10'}`}>
                    {passwordChecks.hasNumber && <Check className="w-3 h-3" />}
                  </div>
                  Numero
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.hasSpecial ? 'text-green-400' : 'text-purple-200/40'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.hasSpecial ? 'bg-green-500/20' : 'bg-purple-500/10'}`}>
                    {passwordChecks.hasSpecial && <Check className="w-3 h-3" />}
                  </div>
                  Carattere speciale
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-purple-200/70 mb-2">
                Conferma Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  data-testid="confirm-password-input"
                  className={`w-full bg-purple-950/50 border text-white rounded-xl px-4 py-3 pr-12 focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-purple-200/30 transition-all ${
                    formData.confirmPassword && formData.confirmPassword !== formData.newPassword
                      ? 'border-red-500/50'
                      : formData.confirmPassword && formData.confirmPassword === formData.newPassword
                      ? 'border-green-500/50'
                      : 'border-purple-500/20'
                  }`}
                  placeholder="Conferma la nuova password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.confirmPassword && formData.confirmPassword !== formData.newPassword && (
                <p className="text-red-400 text-xs mt-2">Le password non corrispondono</p>
              )}
              {formData.confirmPassword && formData.confirmPassword === formData.newPassword && (
                <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Le password corrispondono
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !passwordChecks.minLength || formData.newPassword !== formData.confirmPassword}
              data-testid="reset-password-submit"
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white px-6 py-4 rounded-xl font-bold hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Aggiornamento...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Reimposta Password
                </>
              )}
            </button>
          </form>
        )}

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
        </div>
      </div>
    </main>
  );
}
